/**
 * External dependencies
 */
import { CssTypes, stringify } from '@adobe/css-tools';
import type {
	CssAtRuleAST,
	CssCommentAST,
	CssDeclarationAST,
	CssRuleAST,
	CssStylesheetAST,
} from '@adobe/css-tools';
import type { CssActiveElement, Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import { expandPropName } from './props';

// NOTE. No @nab packages in front.
import type * as nabUtils from '@nab/utils';
type NabUtils = typeof nabUtils;

export function cleanAst( ast: CssStylesheetAST ): CssStylesheetAST {
	const hasErrors = !! ast.stylesheet.parsingErrors?.length;
	if ( hasErrors ) {
		return ast;
	}

	ast = removeOverwrittenDeclarations( ast );
	ast = mergeDuplicatedRules( ast );
	ast = removeEmptyDeclarations( ast );
	ast = removeUnnecessaryEmptyComments( ast );
	ast = cleanEmptyRules( ast );
	ast = removeEmptyDuplicatedRules( ast );
	ast = removeLastEmptyRule( ast );
	return ast;
}

export function appendFallbackRule(
	ast: CssStylesheetAST,
	selector: string
): CssStylesheetAST {
	ast = removeLastEmptyRule( ast );

	const label = getElementLabel( selector );
	const comment = mkComment( ` ${ label } ` );
	const rule: CssRuleAST = {
		type: CssTypes.rule,
		selectors: [ `${ selector }` ],
		declarations: [ mkComment() ],
	};

	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: [
				...ast.stylesheet.rules,
				...( label ? [ comment, rule ] : [ rule ] ),
			],
		},
	};
}

export function containsRule(
	ast: CssStylesheetAST,
	selector: string
): boolean {
	return !! ast.stylesheet.rules.find(
		( r ) =>
			r.type === CssTypes.rule &&
			r.selectors.length === 1 &&
			r.selectors.includes( selector )
	);
}

export function appliesToSelection(
	activeElement: CssActiveElement,
	rule: Maybe< CssAtRuleAST >
): rule is CssRuleAST {
	return (
		rule?.type === CssTypes.rule &&
		rule.selectors.length === 1 &&
		rule.selectors[ 0 ] === activeElement.selector
	);
}

export function getLastRuleForSelection(
	ast: CssStylesheetAST,
	activeElement: CssActiveElement
): Maybe< CssRuleAST > {
	const lastRuleOtherThanFallback = ast.stylesheet.rules.reduce(
		( result, rule, index, array ) =>
			index !== array.length - 1 &&
			appliesToSelection( activeElement, rule )
				? rule
				: result,
		undefined as Maybe< CssRuleAST >
	);

	const lastRule = ast.stylesheet.rules[ ast.stylesheet.rules.length - 1 ];
	const fallbackRule = appliesToSelection( activeElement, lastRule )
		? lastRule
		: undefined;

	return lastRuleOtherThanFallback ?? fallbackRule;
}

export function stringifyAst( ast: CssStylesheetAST ): string {
	return stringify( ast )
		.replaceAll( '*/\n\n', '*/\n' )
		.split( '\n' )
		.map( ( x ) => ( /^\s*\/\*\*\/\s*$/.test( x ) ? '' : x ) )
		.join( '\n' );
}

export function getLastRuleIndex(
	ast: CssStylesheetAST,
	selector: string,
	indexIfNotFound = -1
): number {
	return ast.stylesheet.rules.reduce(
		( result, rule, index, array ) =>
			index !== array.length - 1 &&
			rule.type === CssTypes.rule &&
			rule.selectors.length === 1 &&
			rule.selectors[ 0 ] === selector
				? index
				: result,
		indexIfNotFound
	);
}

export function isOverwrittenDeclaration(
	propNames: ReadonlyArray< string >,
	declaration: CssRuleAST[ 'declarations' ][ number ]
): boolean {
	if ( declaration.type !== CssTypes.declaration ) {
		return false;
	}

	const overwrittingPropGroups = propNames.map( ( x ) =>
		expandPropName( x )
	);

	const currentPropGroup = expandPropName( declaration.property );
	const result = overwrittingPropGroups.some( ( overwrittingProps ) =>
		currentPropGroup.some( ( prop ) => overwrittingProps.includes( prop ) )
	);

	return result;
}

// =======
// HELPERS
// =======

function mergeDuplicatedRules( ast: CssStylesheetAST ): CssStylesheetAST {
	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: ast.stylesheet.rules.reduce(
				( { last, rules }, rule ) => {
					if ( ! isRule( rule ) ) {
						rules.push( rule );
					} else if ( last && haveSameSelectors( last, rule ) ) {
						const l = last;
						rule.declarations.forEach( ( d ) =>
							l.declarations.push( d )
						);
					} else {
						last = rule;
						rules.push( rule );
					}
					return { last, rules };
				},
				{
					last: undefined as Maybe< CssRuleAST >,
					rules: [] as CssAtRuleAST[],
				}
			).rules,
		},
	};
}

function removeOverwrittenDeclarations(
	ast: CssStylesheetAST
): CssStylesheetAST {
	const getPropNames = ( ds: ( CssCommentAST | CssDeclarationAST )[] ) =>
		ds
			.filter( ( x ) => x.type === CssTypes.declaration )
			.map( ( x ) => x.property );

	const rules = [ ...ast.stylesheet.rules ]
		.reverse()
		.reduce( ( result, current ) => {
			if ( ! isRule( current ) ) {
				result.push( current );
				return result;
			}

			let rule: CssRuleAST = current;
			rule = {
				...rule,
				declarations: [ ...rule.declarations ]
					.reverse()
					.reduce(
						( ds, d ) =>
							isOverwrittenDeclaration( getPropNames( ds ), d )
								? ds
								: [ ...ds, d ],
						[] as ( CssCommentAST | CssDeclarationAST )[]
					)
					.reverse(),
			};

			const previous = result
				.filter( isRule )
				.find( ( r ) => haveSameSelectors( rule, r ) );
			if ( ! previous ) {
				result.push( rule );
				return result;
			}

			rule = {
				...rule,
				declarations: rule.declarations.filter(
					( d ) =>
						d.type !== CssTypes.declaration ||
						! previous.declarations.some(
							( x ) =>
								x.type === CssTypes.declaration &&
								x.property === d.property
						)
				),
			};
			result.push( rule );
			return result;
		}, [] as CssAtRuleAST[] )
		.reverse();

	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules,
		},
	};
}

function cleanEmptyRules( ast: CssStylesheetAST ): CssStylesheetAST {
	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: ast.stylesheet.rules.map( ( r ) =>
				isRule( r ) && isEmpty( r )
					? { ...r, declarations: [ ...r.declarations, mkComment() ] }
					: r
			),
		},
	};
}

function removeEmptyDeclarations( ast: CssStylesheetAST ): CssStylesheetAST {
	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: ast.stylesheet.rules.map( ( r ) =>
				isRule( r )
					? {
							...r,
							declarations: r.declarations.filter(
								( d ) =>
									d.type !== CssTypes.declaration ||
									!! d.value.trim()
							),
					  }
					: r
			),
		},
	};
}

function haveSameSelectors( r1: CssRuleAST, r2: CssRuleAST ): boolean {
	return (
		r1.selectors.every( ( s ) => r2.selectors.includes( s ) ) &&
		r2.selectors.every( ( s ) => r1.selectors.includes( s ) )
	);
}

const mkComment = ( comment = '' ): CssCommentAST => ( {
	type: CssTypes.comment,
	comment,
} );

function removeEmptyDuplicatedRules( ast: CssStylesheetAST ): CssStylesheetAST {
	const { rules } = ast.stylesheet;
	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: rules.filter(
				( r ) =>
					! isRule( r ) ||
					! isEmpty( r ) ||
					! rules.some(
						( x ) =>
							isRule( x ) &&
							! isEmpty( x ) &&
							haveSameSelectors( r, x )
					)
			),
		},
	};
}

function removeUnnecessaryEmptyComments(
	ast: CssStylesheetAST
): CssStylesheetAST {
	return {
		...ast,
		stylesheet: {
			...ast.stylesheet,
			rules: ast.stylesheet.rules.map( ( r ): CssAtRuleAST => {
				if ( ! isRule( r ) ) {
					return r;
				}

				if (
					r.declarations.some(
						( d ) => CssTypes.declaration === d.type
					)
				) {
					return {
						...r,
						declarations: r.declarations.filter(
							( d ) =>
								CssTypes.comment !== d.type ||
								!! d.comment.trim()
						),
					};
				}

				return r;
			} ),
		},
	};
}

function removeLastEmptyRule( ast: CssStylesheetAST ): CssStylesheetAST {
	const allRules = ast.stylesheet.rules;

	const lastRule = allRules[ allRules.length - 1 ];
	let rules =
		isRule( lastRule ) && looksEmpty( lastRule )
			? allRules.slice( 0, allRules.length - 1 )
			: allRules;
	while ( rules[ rules.length - 1 ]?.type === CssTypes.comment ) {
		rules = rules.slice( 0, rules.length - 1 );
	}

	return { ...ast, stylesheet: { ...ast, rules } };
}

const isRule = ( rule?: CssAtRuleAST ): rule is CssRuleAST =>
	!! rule && rule.type === CssTypes.rule;

const looksEmpty = ( rule: CssRuleAST ) =>
	rule.declarations.every( ( d ) => d.type === CssTypes.comment );

const isEmpty = ( rule: CssRuleAST ) =>
	! rule.declarations.filter(
		( r ) => r.type === CssTypes.declaration || !! r.comment.trim().length
	).length;

function getElementLabel( s: string ): string {
	if ( hasNabUtils( window.parent ) ) {
		return window.parent.nab.utils.getElementLabel( s );
	}
	if ( hasNabUtils( window ) ) {
		return window.nab.utils.getElementLabel( s );
	}
	return s;
}

const hasNabUtils = (
	w: unknown
): w is {
	readonly nab: {
		readonly utils: NabUtils;
	};
} =>
	// @ts-expect-error Quick and dirty check.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	!! w?.nab?.utils;
